import { QueryFactory } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  pagingQueryParams,
  sortingQueryParams,
  toQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { PageSize } from '@lmt/unicorn-shared/app/components/common/table/types';
import {
  dateRangeSettingsToQueryParams,
  PatientListFiltersData,
} from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { PatientFilter } from '@lmt/unicorn-shared/app/components/patients/list/components/PatientFilter';
import { PatientList } from '@lmt/unicorn-shared/app/components/patients/list/components/PatientList';
import { PatientListConfigurations } from '@lmt/unicorn-shared/app/components/patients/list/configuration/PatientListConfigurations';
import { patientListParams } from '@lmt/unicorn-shared/app/components/patients/list/patientListParams';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { useResetFilters } from '@lmt/unicorn-shared/app/helpers/filter-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import equal from 'fast-deep-equal/react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { NumberParam, StringParam, withDefault } from 'react-router-url-params';
import { TablePagination } from '../../common/table/TablePagination';
import { TableTitle } from '../../common/table/TableTitle';
import { SearchInput } from '../../common/ui-kit/inputs/search/SearchInput';
import { FiltersButton } from './components/FiltersButton';
import { SaveButton } from './components/SaveButton';

import styles from './PatientListPage.module.scss';
import { getAdditionalSorting } from '../common';
import { useEventCallback } from '@mui/material';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';

function mapAlike(
  object: Record<string, unknown>,
  alike: Record<string, unknown>,
) {
  const copy = { ...object };
  const alikeKeys = new Set(Object.keys(alike));
  for (const key of Object.keys(copy))
    if (!alikeKeys.has(key)) delete copy[key];
  return copy;
}

/*
 * Returns true if properties that exist in both objects have same values.
 */
function areSamePropertiesEqual(firstObject: any, secondObject: any): boolean {
  if (!firstObject || !secondObject) return false;
  const secondCopy: Record<string, unknown> = {};
  const firstCopy: Record<string, unknown> = {};
  const secondObjectKeys = new Set(Object.keys(secondObject));

  Object.keys(firstObject).forEach((key) => {
    if (secondObjectKeys.has(key)) {
      secondCopy[key] = secondObject[key];
      firstCopy[key] = firstObject[key];
    }
  });
  return equal(firstCopy, secondCopy);
}

export const PatientListPage: FC = React.memo(function PatientListPage() {
  const isFixedTab = useTabs().isFixedTab;
  const dispatch = useUniDispatch();
  const modals = useModal();
  const [queryParams, setQueryParams] = useQueryParamsInTab({
    search: StringParam,
    configurationId: withDefault(NumberParam, undefined! as number),
    ...patientListParams,
    ...pagingQueryParams(PageSize.Small),
    ...sortingQueryParams(),
  });
  const [searchText, setSearchText] = useState<string>(
    queryParams.search ?? '',
  );

  const [searchTextDebounced] = useDebounce(searchText, 500, {
    trailing: true,
  });

  useEffect(
    () => setQueryParams({ search: searchTextDebounced, page: undefined }),
    [searchTextDebounced],
  );

  const [filterQueryParameters] = useQueryParamsInTab(patientListParams);

  const defaultConfigurationId =
    useUniSelector(getUIConfigurations).patientListConfigurationId ?? undefined;

  const { data: configurations } =
    QueryFactory.PatientListConfigurationQuery.useGetPatientListConfigurationsQuery();

  const possibleConfigurations = [
    queryParams.configurationId,
    defaultConfigurationId,
    configurations?.[0]?.id,
  ];
  const configurationId = useMemo(
    () =>
      findFirstExistingConfiguration(possibleConfigurations, configurations),
    [configurations, ...possibleConfigurations],
  );

  const configurationQuery =
    QueryFactory.PatientListConfigurationQuery.useGetPatientListConfigurationQuery(
      configurationId!,
      { enabled: !!configurationId },
    );
  const configuration = configurationQuery.data;

  const handleConfigurationChange = useEventCallback(
    (newConfigurationId: number) => {
      if (isFixedTab) {
        dispatch(
          UIConfigurationsActions.patientListConfigurationIdChanged(
            newConfigurationId,
          ),
        );
      }
      setQueryParams({ configurationId: newConfigurationId, page: undefined });
    },
  );

  useEffect(() => {
    if (defaultConfigurationId && !configurationId) {
      handleConfigurationChange(defaultConfigurationId);
    }
  }, []);

  const i18next = useScopedTranslation('patients.list');

  const {
    hasFilterBySubtenants,
    hasFilterByStatuses,
    hasFilterByStates,
    hasFilterByTags,
    hasFilterBySexTypes,
    hasFilterByLastActivity,
    hasFilterByLastVisit,
    hasFilterBySqlFilter,
  } = queryParams ?? {};

  const patientsQuery = QueryFactory.PatientQuery.useGetPatientsQuery(
    {
      searchQuery: queryParams.search,
      subtenantIds: hasFilterBySubtenants
        ? (queryParams.subtenants as number[])
        : undefined,
      tagIds: hasFilterByTags ? (queryParams.tags as number[]) : undefined,
      states: hasFilterByStates ? queryParams.states : undefined,
      statuses: hasFilterByStatuses ? queryParams.statuses : undefined,
      sexTypes: hasFilterBySexTypes ? queryParams.sexTypes : undefined,
      sqlFilterQuery: hasFilterBySqlFilter ? queryParams.sqlFilter : undefined,
      ...dateRangeSettingsToQueryParams({
        lastVisit: hasFilterByLastVisit
          ? (queryParams.lastVisit ?? undefined)
          : undefined,
        lastActivity: hasFilterByLastActivity
          ? (queryParams.lastActivity ?? undefined)
          : undefined,
      }),
      ...toQueryParams(queryParams),
      additionalSorting: getAdditionalSorting(
        queryParams.sortBy,
        queryParams.desc,
      ),
    },
    {
      retry: false,
      throwOnError: false,
    },
  );
  const sqlQueryError = (
    (patientsQuery.error as any)?.detail as string
  )?.includes('SQL')
    ? ((patientsQuery.error as any)?.detail as string)
    : undefined;

  const resetConfigurationValues = useEventCallback(
    (configuration: PatientListFiltersData) => {
      if (!configuration) {
        return;
      }
      setQueryParams(mapAlike(configuration, filterQueryParameters));
    },
  );

  useResetFilters(
    configuration!,
    (configuration) => {
      resetConfigurationValues(configuration);
    },
    (configuration) => {
      resetConfigurationValues(configuration);
    },
  );

  useEffect(() => {
    if (configuration) {
      resetConfigurationValues(configuration);
    }
  }, [configuration]);

  const hasUserChanges = useMemo(
    () => !areSamePropertiesEqual(configuration, filterQueryParameters),
    [configuration, filterQueryParameters],
  );

  const hasAppliedFilters =
    (hasFilterBySubtenants && (queryParams.subtenants?.length ?? 0) > 0) ||
    (hasFilterByTags && (queryParams.tags?.length ?? 0) > 0) ||
    (hasFilterByStates && (queryParams.states?.length ?? 0) > 0) ||
    (hasFilterByStatuses && (queryParams.statuses?.length ?? 0) > 0) ||
    (hasFilterBySexTypes && (queryParams.sexTypes?.length ?? 0) > 0) ||
    (hasFilterByLastVisit && !!queryParams.lastVisit) ||
    (hasFilterByLastActivity && !!queryParams.lastActivity) ||
    (hasFilterBySqlFilter && !!queryParams.sqlFilter);

  if (!configuration) {
    return <Loading loading={true}></Loading>;
  }
  return (
    <div className={styles.pageContainer} data-test-id="table-page-container">
      <div className={styles.tableToolbar} data-test-id="table-page-toolbar">
        <div className={styles.toolbar}>
          <TableTitle
            title={i18next.t('title')}
            count={patientsQuery.data?.totalCount}
          />
          <PatientListConfigurations
            onConfigurationChange={handleConfigurationChange}
            configurations={configurations}
            currentConfigurationId={configurationId}
          />
          {hasUserChanges && configurationId ? (
            <SaveButton
              configurationId={configurationId}
              values={{
                ...filterQueryParameters,
                name: configuration.name,
                isShared: configuration.isShared,
              }}
              onNewConfiguration={(configurationId) =>
                handleConfigurationChange(configurationId)
              }
            />
          ) : null}
        </div>
        <div className={styles.toolbar}>
          <SearchInput
            className={styles.searchInput}
            bordered={true}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value ?? null);
            }}
            placeholder={i18next.t('search_placeholder')}
          />
          <FiltersButton
            className={styles.filtersButton}
            isFilterApplied={!!hasAppliedFilters}
            isFiltersBlockShown={!!filterQueryParameters?.isFilterDisplayed}
            onClick={() =>
              setQueryParams((prevState) => ({
                isFilterDisplayed: !prevState?.isFilterDisplayed,
              }))
            }
          />
        </div>
      </div>
      <div
        className={
          filterQueryParameters?.isFilterDisplayed
            ? styles.tableContainer
            : styles.tableContainerOnly
        }
      >
        <PatientList
          patients={patientsQuery.data?.data ?? undefined}
          setSorting={setQueryParams}
          isLoading={patientsQuery.isLoading}
          isFiltered={!!queryParams.search || !!hasAppliedFilters}
        />
        <div style={{ marginTop: '-10px' }}>
          <TablePagination
            pagination={queryParams}
            pageLimitTitle={i18next.t('pagination_page_limit')}
            changePagination={setQueryParams}
            totalCount={patientsQuery.data?.totalCount}
          />
        </div>
      </div>
      {filterQueryParameters?.isFilterDisplayed && (
        <div className={styles.filtersBlockContainer}>
          <PatientFilter
            values={filterQueryParameters}
            onChange={(params) => {
              setQueryParams({ page: undefined, ...params });
            }}
            sqlError={sqlQueryError}
            hasUserChanges={hasUserChanges}
            onConfigurationReset={() => {
              resetConfigurationValues(configuration);
            }}
          />
        </div>
      )}
    </div>
  );
});
function findFirstExistingConfiguration(
  possibleConfigurations: (number | undefined)[],
  configurations: QueryFactory.PatientListConfigurationListDto[] | undefined,
) {
  for (const configurationId of possibleConfigurations) {
    if (
      configurationId &&
      configurations?.find((x) => x.id === configurationId)
    )
      return configurationId;
  }
  return configurations?.[0]?.id;
}
